/**
 * 마크다운 본문에서 `<img>` 를 렌더할 때 쓰는 컴포넌트.
 *
 * - lazy loading
 * - 로드 실패 시 alt 텍스트 + 깨진 아이콘 placeholder 로 fallback (빈 공간 안 보이게)
 * - WikiMarkdown 과 chat Markdown 양쪽에서 공통 사용.
 * - onDelete 가 주어지면 hover 시 X 버튼 노출 — admin 의 자료 preview 에서 의미없는 figure 즉시 정리.
 */
import { X } from 'lucide-react';
import { useState } from 'react';

interface MarkdownImgProps {
  src?: string;
  alt?: string;
  /** 정의되어 있으면 이미지 위 호버 시 X 버튼 노출. src 의 마지막 path 토큰 (filename) 을 인자로 호출. */
  onDelete?: (filename: string) => void;
}

export function MarkdownImg({ src, alt, onDelete }: MarkdownImgProps) {
  const [errored, setErrored] = useState(false);
  if (errored || !src) {
    return (
      <span className="my-3 inline-flex items-center gap-2 rounded border border-border bg-surface px-2 py-1 text-xs text-foreground-muted">
        <span aria-hidden>🖼️</span>
        <span>이미지를 불러올 수 없습니다{alt ? `: ${alt}` : ''}</span>
      </span>
    );
  }
  if (!onDelete) {
    return (
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        className="my-3 max-w-full rounded border border-border"
        onError={() => setErrored(true)}
      />
    );
  }
  const filename = src.split(/[/\\]/).pop() ?? '';
  return (
    <span className="group relative my-3 inline-block">
      <img
        src={src}
        alt={alt ?? ''}
        loading="lazy"
        className="max-w-full rounded border border-border"
        onError={() => setErrored(true)}
      />
      <button
        type="button"
        onClick={() => {
          if (
            window.confirm(
              `이 이미지를 자료에서 영구 삭제합니다.\n파일: ${filename}\n${alt ? `캡션: ${alt}\n` : ''}계속하시겠습니까?`,
            )
          ) {
            onDelete(filename);
          }
        }}
        title="이 이미지를 자료에서 삭제"
        className="absolute right-2 top-2 hidden rounded-full bg-destructive p-1 text-white shadow-md hover:bg-destructive/90 group-hover:inline-flex"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
